import { getBaseURL, getClient } from '../BLL/clients/HttpClient';
import { ApiEndPoint } from '../BLL/Enums/ApiEndPoint';

export interface MedicalRecord {
  id: number;
  requestForReleaseFormId: number | null;
  caseId: string | null;
  docUrl: string;
  fileName: string;
  uploadedBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface MedicalRecordsResponse {
  medicalRecords: MedicalRecord[];
}

export interface UploadMedicalRecordsResponse {
  message: string;
  records: MedicalRecord[];
}

export interface DeleteMedicalRecordResponse {
  message: string;
}

// Get medical records by case ID or form ID
export const getMedicalRecords = async (
  caseId?: string
): Promise<MedicalRecordsResponse> => {
  const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: authBaseUrl });
  const { data } = await client.get<MedicalRecordsResponse>(
    `v1/release-forms/medical-records/${caseId}`
  );

  const formattedData = {
    ...data,
    medicalRecords: data.medicalRecords.map((record) => ({
      ...record,
      createdAt: new Date(record.createdAt),
      updatedAt: new Date(record.updatedAt),
    })),
  };

  return formattedData;
};

// Upload medical records
export const uploadMedicalRecords = async (
  files: File[],
  caseId?: string,
  formId?: number
): Promise<UploadMedicalRecordsResponse> => {
  const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: authBaseUrl });

  const formData = new FormData();

  // Add files to form data
  files.forEach((file) => {
    formData.append('files', file);
  });

  // Add case ID or form ID to form data
  if (caseId) {
    formData.append('caseId', caseId);
  }

  if (formId) {
    formData.append('formId', formId.toString());
  }

  const { data } = await client.post<UploadMedicalRecordsResponse>(
    'v1/release-forms/medical-records',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  // Format dates
  const formattedData = {
    ...data,
    records: data.records.map((record) => ({
      ...record,
      createdAt: new Date(record.createdAt),
      updatedAt: new Date(record.updatedAt),
    })),
  };

  return formattedData;
};

// Delete a medical record
export const deleteMedicalRecord = async (
  id: number
): Promise<DeleteMedicalRecordResponse> => {
  const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: authBaseUrl });

  const { data } = await client.delete<DeleteMedicalRecordResponse>(
    `v1/release-forms/medical-records/${id}`
  );

  return data;
};
